"use client";

import React, { useState, useTransition } from "react";
import { VisitListItemProps } from "./types";
import { defaultVisitListItemTheme } from "./themes";
import { mergeTheme } from "../utils/theme";
import { AvatarGroup } from "./avatar-group";
import { VisitStatus } from "./visit-status";

export const VisitListItem: React.FC<VisitListItemProps> = ({
  visit,
  onClick,
  showNotificationCard = false,
  isClickable = true,
  theme,
  className = "",
  avatarComponent: CustomAvatar,
  statusComponent: CustomStatus,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isPending, startTransition] = useTransition();
  const mergedTheme = mergeTheme(defaultVisitListItemTheme, theme);

  // Determine if visit is clickable based on status
  const shouldBeClickable =
    isClickable && (visit.status === "active" || visit.status === "completed");

  // Get container classes based on state
  const getContainerClasses = () => {
    const classes = [mergedTheme.container?.base];

    if (shouldBeClickable) {
      classes.push(mergedTheme.container?.clickable);
    }

    if (isHovered && shouldBeClickable) {
      classes.push(mergedTheme.container?.hover);
    }

    if (isActive && shouldBeClickable) {
      classes.push(mergedTheme.container?.active);
    }

    return classes.filter(Boolean).join(" ");
  };

  // Format patient and clinician names
  const getPatientName = () => {
    if (visit.patientFirstName && visit.patientLastName) {
      return `${visit.patientFirstName} ${visit.patientLastName}`;
    }
    return visit.patientName || "Patient";
  };

  const getClinicianName = () => {
    if (visit.clinicianFirstName && visit.clinicianLastName) {
      return `Dr. ${visit.clinicianFirstName} ${visit.clinicianLastName}`;
    }
    return visit.clinicianName || "";
  };

  // Get title based on status
  const getTitle = () => {
    const patientName = getPatientName();
    const clinicianName = getClinicianName();

    switch (visit.status) {
      case "requested":
        return `Reviewing ${patientName}'s Details`;
      case "active":
        return clinicianName
          ? `${patientName} & ${clinicianName}`
          : `${patientName}'s Visit`;
      case "completed":
        return clinicianName
          ? `${patientName} & ${clinicianName}`
          : patientName;
      case "cancelled":
        return patientName;
      default:
        return `${patientName}'s Visit`;
    }
  };

  // Get subtitle based on status
  const getSubtitle = () => {
    switch (visit.status) {
      case "requested":
        return "Request details";
      case "active":
        return "Your visit details";
      case "completed":
        return "Visit summary";
      case "cancelled":
        return "Cancelled Visit";
      default:
        return visit.lastMessage || "No recent activity";
    }
  };

  // Handle click
  const handleClick = () => {
    if (shouldBeClickable && onClick) {
      startTransition(() => {
        onClick(visit);
      });
    }
  };

  // Handle keyboard interaction
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (shouldBeClickable && onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick(visit);
    }
  };

  // Determine avatar state
  const getAvatarState = () => {
    if (visit.status === "requested" || !visit.clinicianName) {
      return "not-assigned";
    }
    return "assigned";
  };

  // Render avatar component
  const AvatarComponent = CustomAvatar || AvatarGroup;
  const StatusComponent = CustomStatus || VisitStatus;

  // Render notification card content
  const renderNotificationContent = () => {
    if (!showNotificationCard) return null;

    let message = "";
    let iconColor = "";

    switch (visit.status) {
      case "requested":
        message =
          "You can close the app, we will notify you as soon as the doctor has responded";
        iconColor = "#10b981"; // green
        break;
      default:
        return null;
    }

    if (!message) return null;

    return (
      <div
        className={`notification-card ${mergedTheme.notificationCard?.container}`}
      >
        <svg
          className={mergedTheme.notificationCard?.icon}
          style={{ color: iconColor }}
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          {visit.status === "requested" ? (
            <path d="M6 10.17L2.83 7 1.41 8.41 6 13 15 4 13.59 2.59z" />
          ) : (
            <path d="M8 0C3.58 0 0 3.58 0 8c0 1.42.37 2.75 1.02 3.9L0 16l4.1-1.02C5.25 15.63 6.58 16 8 16c4.42 0 8-3.58 8-8s-3.58-8-8-8z" />
          )}
        </svg>
        <span className={mergedTheme.notificationCard?.text}>{message}</span>
      </div>
    );
  };

  // Main card content
  const cardContent = (
    <div
      className={`visit-list-item ${visit.status} ${getContainerClasses()} ${className}`.trim()}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      role={shouldBeClickable ? "button" : undefined}
      tabIndex={shouldBeClickable ? 0 : undefined}
    >
      <div className={`visit-content ${mergedTheme.content?.container}`}>
        <div className="visit-header">
          <AvatarComponent
            firstAvatar={visit.patientName || "P"}
            secondAvatar={visit.clinicianName || "unassignedDoc"}
            size="medium"
            layout="horizontal"
          />
          <StatusComponent
            status={visit.status}
            hasUnreadMessages={visit.hasUnreadMessages}
            timestamp={visit.timestamp}
          />
        </div>

        <div className="visit-body">
          <h3 className={`visit-title ${mergedTheme.content?.title}`}>
            {getTitle()}
          </h3>
          <p className={`visit-subtitle ${mergedTheme.content?.subtitle}`}>
            {getSubtitle()}
          </p>
        </div>
      </div>

      {shouldBeClickable && (
        <>
          {isPending ? (
            <svg
              className={`chevron-icon ${mergedTheme.chevron} animate-spin`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              className={`chevron-icon ${mergedTheme.chevron}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          )}
        </>
      )}
    </div>
  );

  // Wrap with notification card if needed
  if (
    showNotificationCard &&
    (visit.status === "requested" ||
      (visit.status === "active" && visit.hasUnreadMessages))
  ) {
    return (
      <div className="visit-list-item-wrapper">
        {cardContent}
        {renderNotificationContent()}
      </div>
    );
  }

  return cardContent;
};
